// ─── UDISEAnomalyDetector.tsx ─────────────────────────────────────────────────

import React, { useState, useRef, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import ClassSectionFilter from '../Students/parts/ClassSectionFilter';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import StudentsListSkeletonLoader from '../../skeletons/StudentsListSkeletonLoader';

import ClassMappingModal from './anomalyDetector/ClassMappingModal';
import FieldMappingModal from './anomalyDetector/FieldMappingModal';
import {
  ClassData, StudentData, UDISERow, FieldMapping, FieldAnomaly,
  StudentAnomaly, AnomalyType, ModalView,
  DB_FIELDS, DEFAULT_MAPPINGS, STATUS_CONFIG,
} from './anomalyDetector/types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function romanToArabic(str: string): number | null {
  const map: Record<string, number> = { I:1,V:5,X:10,L:50,C:100,D:500,M:1000 };
  const s = str.trim().toUpperCase();
  if (!/^[IVXLCDM]+$/.test(s)) return null;
  let result = 0;
  for (let i = 0; i < s.length; i++) {
    const cur = map[s[i]], next = map[s[i + 1]];
    if (next && cur < next) result -= cur; else result += cur;
  }
  return result;
}

function normalizeClassName(raw: string): string {
  const s = raw.trim().replace(/^class\s*/i, '');
  const arabic = romanToArabic(s);
  return arabic !== null ? String(arabic) : s.toLowerCase();
}

const normalize = (v: string) => (v ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

function exportReport(results: StudentAnomaly[], mappings: FieldMapping[]) {
  const em = mappings.filter(m => m.enabled && m.dbField && m.udiseField);
  const headers = ['Student Name','Class','Section','Status',...em.map(m=>`DB: ${m.label||m.dbField}`),...em.map(m=>`UDISE: ${m.udiseField}`)];
  const rows = results.map(r => {
    const name = r.dbStudent?.name || r.udiseRow?.['Name'] || r.matchKey;
    const cls  = r.dbStudent?.class_name || r.udiseRow?.['Class'] || '';
    const sec  = r.dbStudent?.section    || r.udiseRow?.['Section'] || '';
    return [name, cls, sec, r.overallStatus,
      ...em.map(m => r.dbStudent ? String((r.dbStudent as any)[m.dbField] ?? '') : ''),
      ...em.map(m => r.udiseRow  ? (r.udiseRow[m.udiseField] ?? '') : ''),
    ];
  });
  const csv = [headers,...rows].map(r=>r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
  a.download = `udise_anomaly_report_${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
}

// ─── CSV Upload Step ───────────────────────────────────────────────────────────

const CSVUploadStep: React.FC<{ onParsed: (headers: string[], rows: UDISERow[]) => void }> = ({ onParsed }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');

  const parseCSV = (text: string) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) { setError('File must have at least a header row and one data row.'); return; }
    const firstLine = lines[0];
    const delim = (firstLine.match(/\t/g)||[]).length > (firstLine.match(/,/g)||[]).length ? '\t' : ',';
    const headers = firstLine.split(delim).map(h => h.trim().replace(/^"|"$/g,''));
    const rows: UDISERow[] = [];
    for (let i = 1; i < lines.length; i++) {
      const vals = lines[i].split(delim).map(v => v.trim().replace(/^"|"$/g,''));
      if (vals.length < 2) continue;
      const row: UDISERow = {};
      headers.forEach((h,idx) => { row[h] = vals[idx] ?? ''; });
      rows.push(row);
    }
    if (rows.length === 0) { setError('No data rows found in the file.'); return; }
    setError('');
    onParsed(headers, rows);
  };

  const handleFile = (file: File) => {
    if (!file.name.match(/\.(csv|tsv|txt)$/i)) { setError('Please upload a CSV or TSV file.'); return; }
    const reader = new FileReader();
    reader.onload = e => parseCSV(e.target?.result as string);
    reader.readAsText(file);
  };

  return (
    <div>
      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
        onClick={() => fileRef.current?.click()}
        style={{
          border: `2px dashed ${dragging ? '#3b82f6' : '#bfdbfe'}`,
          borderRadius: 14, padding: '32px 16px', textAlign: 'center',
          cursor: 'pointer', background: dragging ? '#eff6ff' : '#f8fafc', transition: 'all .2s',
        }}
      >
        <div style={{
          width: 48, height: 48, borderRadius: 13,
          background: 'linear-gradient(135deg,#1e3a8a,#3b82f6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 12px', boxShadow: '0 6px 18px rgba(59,130,246,.25)',
          border: '2.5px solid white',
        }}>
          <i className="pi pi-file-import" style={{ fontSize: 20, color: 'white' }} />
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#1e40af', marginBottom: 4 }}>Upload UDISE Data File</div>
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 3 }}>Drag & drop or click to browse</div>
        <div style={{ fontSize: 12, color: '#94a3b8' }}>Supports CSV (.csv) or Tab-separated (.tsv / .txt)</div>
        <input ref={fileRef} type="file" accept=".csv,.tsv,.txt" style={{ display: 'none' }}
          onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }} />
      </div>

      {error && (
        <div style={{ marginTop: 10, padding: '10px 14px', borderRadius: 12, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 13, fontWeight: 500 }}>
          <i className="pi pi-exclamation-circle" style={{ marginRight: 6 }} />{error}
        </div>
      )}

      <div style={{ marginTop: 14, padding: '12px 16px', borderRadius: 12, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#1e40af', marginBottom: 6 }}>
          <i className="pi pi-info-circle" style={{ marginRight: 5 }} />Expected UDISE columns:
        </div>
        <div style={{ fontSize: 12, color: '#1e3a8a', fontFamily: 'monospace', lineHeight: 2 }}>
          Class · Section · Name · Gender · Student PEN · Father Name · Mother Name · AADHAAR No. · APAAR ID · ...
        </div>
      </div>
    </div>
  );
};

// ─── Anomaly Results Modal ─────────────────────────────────────────────────────

type FilterKey = 'all' | 'anomaly' | 'missing_in_db' | 'missing_in_udise' | 'match';

interface AnomalyResultsModalProps {
  visible: boolean;
  onHide: () => void;
  results: StudentAnomaly[];
  mappings: FieldMapping[];
}

const AnomalyResultsModal: React.FC<AnomalyResultsModalProps> = ({ visible, onHide, results, mappings }) => {
  const [filter, setFilter] = useState<FilterKey>('all');
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setFilter('all');
    setSearch('');
    setExpandedKey(null);
  }, [results]);

  const counts = {
    anomaly:          results.filter(r => r.overallStatus === 'anomaly').length,
    missing_in_db:    results.filter(r => r.overallStatus === 'missing_in_db').length,
    missing_in_udise: results.filter(r => r.overallStatus === 'missing_in_udise').length,
    match:            results.filter(r => r.overallStatus === 'match').length,
  };

  const filtered = results.filter(r => {
    if (filter !== 'all' && r.overallStatus !== filter) return false;
    if (!search.trim()) return true;
    const name = r.dbStudent?.name
      ?? (r.udiseRow ? Object.entries(r.udiseRow).find(([k]) => k.toLowerCase().includes('name'))?.[1] : undefined)
      ?? r.matchKey;
    return (name ?? '').toLowerCase().includes(search.toLowerCase());
  });

  const hasAnomalies = results.some(r => r.overallStatus === 'anomaly');

  // Filter chip definitions — order matters (left to right)
  const FILTER_CHIPS: { key: FilterKey; label: string; icon: string; color: string; bg: string; border: string }[] = [
    { key: 'all',            label: 'All',         icon: 'pi pi-list',              color: '#374151', bg: '#f3f4f6',  border: '#e5e7eb'  },
    { key: 'anomaly',        label: 'Anomaly',     icon: 'pi pi-exclamation-circle', color: '#dc2626', bg: '#fef2f2', border: '#fecaca'  },
    { key: 'missing_in_db',  label: 'Not in DB',   icon: 'pi pi-user-minus',         color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe'  },
    { key: 'missing_in_udise', label: 'Not in UDISE', icon: 'pi pi-file-excel',      color: '#ea580c', bg: '#fff7ed', border: '#fed7aa'  },
    { key: 'match',          label: 'Match',       icon: 'pi pi-check-circle',       color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0'  },
  ];

  const getChipCount = (key: FilterKey) => key === 'all' ? results.length : counts[key as keyof typeof counts];

  const resultsHeader = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '4px 0' }}>
      <div style={{
        width: 48, height: 48, borderRadius: 13,
        background: hasAnomalies
          ? 'linear-gradient(135deg,#991b1b,#dc2626)'
          : 'linear-gradient(135deg,#14532d,#16a34a)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0, boxShadow: hasAnomalies ? '0 6px 18px rgba(220,38,38,.25)' : '0 6px 18px rgba(22,163,74,.25)',
        border: '2.5px solid white',
      }}>
        <i className="pi pi-chart-bar" style={{ fontSize: 20, color: 'white' }} />
      </div>
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color: '#1e40af' }}>Anomaly Report</div>
        <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
          {counts.anomaly} anomalies &nbsp;·&nbsp; {counts.match} matches &nbsp;·&nbsp; {results.length} total
        </div>
      </div>
    </div>
  );

  return (
    <Dialog
      visible={visible} onHide={onHide} header={resultsHeader}
      style={{ width: '95vw', maxWidth: 1000, borderRadius: 22, overflow: 'hidden' }}
      contentStyle={{ padding: '18px 24px 8px', background: 'linear-gradient(180deg,#f8fafc,#f1f5f9)' }}
      headerStyle={{ background: 'white', borderBottom: '1px solid #e5e7eb', padding: '18px 24px', borderRadius: '22px 22px 0 0' }}
      modal draggable={false} resizable={false} blockScroll
    >
      <style>{`
        .ar-chip {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 7px 14px; border-radius: 20px; cursor: pointer;
          border: 1.5px solid #e5e7eb; background: white;
          font-size: 13px; font-weight: 600; color: #374151;
          transition: all .15s; user-select: none; white-space: nowrap;
        }
        .ar-chip:hover { transform: translateY(-1px); box-shadow: 0 2px 8px rgba(0,0,0,.08); }
        .ar-chip.active { box-shadow: 0 2px 10px rgba(0,0,0,.1); }
        .ar-result-row {
          border-radius: 14px; background: white;
          box-shadow: 0 1px 4px rgba(0,0,0,.06);
          border: 1.5px solid #e2e8f0;
          transition: border-color .15s, background .15s;
        }
        .ar-result-row.expanded { border-color: var(--row-border); background: var(--row-bg); }
        .ar-row-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 12px 16px; cursor: pointer; min-height: 56px;
        }
        .ar-row-header:hover { background: rgba(0,0,0,.015); }
        .ar-field-grid-header {
          display: grid; grid-template-columns: 120px 1fr 1fr;
          gap: 8px; padding: 6px 10px;
          background: #f1f5f9; border-radius: 8px; margin-bottom: 6px;
          border: 1px solid #e2e8f0;
        }
        .ar-field-grid-row {
          display: grid; grid-template-columns: 120px 1fr 1fr;
          gap: 8px; padding: 7px 10px; border-radius: 8px; margin-bottom: 4px;
        }
      `}</style>

      {/* ── Filter chips ──────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {FILTER_CHIPS.map(chip => {
          const isActive = filter === chip.key;
          const count = getChipCount(chip.key);
          return (
            <div
              key={chip.key}
              className={`ar-chip${isActive ? ' active' : ''}`}
              onClick={() => setFilter(isActive && chip.key !== 'all' ? 'all' : chip.key)}
              style={{
                borderColor: isActive ? chip.border : '#e5e7eb',
                background: isActive ? chip.bg : 'white',
                color: isActive ? chip.color : '#374151',
              }}
            >
              <i className={chip.icon} style={{ fontSize: 12, color: isActive ? chip.color : '#9ca3af' }} />
              {chip.label}
              <span style={{
                padding: '1px 7px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                background: isActive ? `${chip.color}20` : '#f3f4f6',
                color: isActive ? chip.color : '#6b7280',
              }}>
                {count}
              </span>
            </div>
          );
        })}
      </div>

      {/* ── Search bar ────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        background: 'white', borderRadius: 12, padding: '10px 14px',
        border: '1.5px solid #dbeafe', marginBottom: 14,
        boxShadow: '0 1px 4px rgba(0,0,0,.04)',
      }}>
        <i className="pi pi-search" style={{ fontSize: 13, color: '#3b82f6' }} />
        <input
          type="text" placeholder="Search by student name..."
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13, background: 'transparent', color: '#111827' }}
        />
        <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, whiteSpace: 'nowrap' }}>
          {filtered.length} result{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* ── Results list ──────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 420, overflowY: 'auto', paddingRight: 4 }}>
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px 16px', color: '#94a3b8', fontSize: 13 }}>
            No results for this filter
          </div>
        )}

        {filtered.map((result, listIdx) => {
          const cfg = STATUS_CONFIG[result.overallStatus];
          const stableKey = `${result.matchKey}__${listIdx}`;
          const isExpanded = expandedKey === stableKey;

          const udiseNameValue = result.udiseRow
            ? (Object.entries(result.udiseRow).find(([k]) => k.toLowerCase().includes('name'))?.[1] || result.matchKey)
            : result.matchKey;
          const displayName = result.dbStudent?.name || udiseNameValue || result.matchKey || '—';
          const anomalyCount = result.anomalies.filter(a => a.type === 'mismatch').length;

          return (
            <div
              key={stableKey}
              className={`ar-result-row${isExpanded ? ' expanded' : ''}`}
              style={{
                '--row-border': cfg.border,
                '--row-bg': cfg.bg,
              } as React.CSSProperties}
            >
              {/* Row header */}
              <div className="ar-row-header" onClick={() => setExpandedKey(isExpanded ? null : stableKey)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                  {/* Status icon badge */}
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                    background: cfg.bg, border: `1.5px solid ${cfg.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <i className={cfg.icon} style={{ fontSize: 14, color: cfg.color }} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {displayName}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                      {result.dbStudent
                        ? `Class ${result.dbStudent.class_name}${result.dbStudent.section ? ` – ${result.dbStudent.section}` : ''} · Roll ${result.dbStudent.roll_no}`
                        : result.udiseRow
                          ? `UDISE: ${Object.values(result.udiseRow).slice(0,3).filter(Boolean).join(' · ')}`
                          : '—'
                      }
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  {result.overallStatus === 'anomaly' && anomalyCount > 0 && (
                    <span style={{ padding: '3px 10px', borderRadius: 20, background: '#fee2e2', color: '#dc2626', fontSize: 11, fontWeight: 700 }}>
                      {anomalyCount} mismatch{anomalyCount !== 1 ? 'es' : ''}
                    </span>
                  )}
                  <span style={{ padding: '4px 11px', borderRadius: 20, background: cfg.bg, color: cfg.color, fontSize: 11, fontWeight: 700, border: `1.5px solid ${cfg.border}` }}>
                    {cfg.label}
                  </span>
                  <i className={`pi pi-chevron-${isExpanded ? 'up' : 'down'}`} style={{ fontSize: 10, color: '#94a3b8' }} />
                </div>
              </div>

              {/* Expanded detail panel */}
              {isExpanded && (
                <div style={{ borderTop: `1px solid ${cfg.border}`, padding: '14px 16px' }}>
                  {result.overallStatus === 'missing_in_db' && (
                    <div style={{ padding: '10px 14px', borderRadius: 10, background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8', fontSize: 13, fontWeight: 500, marginBottom: 10 }}>
                      <i className="pi pi-info-circle" style={{ marginRight: 6 }} />
                      This student exists in UDISE but was <strong>not found</strong> in the school database for the selected class/section.
                    </div>
                  )}
                  {result.overallStatus === 'missing_in_udise' && (
                    <div style={{ padding: '10px 14px', borderRadius: 10, background: '#fff7ed', border: '1px solid #fed7aa', color: '#c2410c', fontSize: 13, fontWeight: 500, marginBottom: 10 }}>
                      <i className="pi pi-info-circle" style={{ marginRight: 6 }} />
                      This student is in the school database but was <strong>not found</strong> in the UDISE file.
                    </div>
                  )}

                  {result.anomalies.length > 0 && (
                    <>
                      <div className="ar-field-grid-header">
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.5px' }}>Field</div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                          <i className="pi pi-database" style={{ marginRight: 4 }} />School DB
                        </div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#1d4ed8', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                          <i className="pi pi-file" style={{ marginRight: 4 }} />UDISE
                        </div>
                      </div>

                      {result.anomalies.map((fa, fidx) => {
                        const isMismatch = fa.type === 'mismatch';
                        return (
                          <div key={fidx} className="ar-field-grid-row" style={{
                            background: isMismatch ? '#fef2f2' : fidx % 2 === 0 ? '#fafafa' : 'white',
                            border: isMismatch ? '1px solid #fecaca' : '1px solid #f1f5f9',
                          }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: 5 }}>
                              {isMismatch && <i className="pi pi-exclamation-triangle" style={{ fontSize: 10, color: '#dc2626' }} />}
                              {fa.type === 'match' && <i className="pi pi-check" style={{ fontSize: 10, color: '#16a34a' }} />}
                              {fa.label || fa.dbField}
                            </div>
                            <div style={{ fontSize: 12, wordBreak: 'break-word', color: isMismatch ? '#dc2626' : '#1f2937', fontWeight: isMismatch ? 600 : 400, padding: '2px 6px', borderRadius: 6, background: isMismatch ? '#fee2e2' : 'transparent' }}>
                              {fa.dbValue || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>—</span>}
                            </div>
                            <div style={{ fontSize: 12, wordBreak: 'break-word', color: isMismatch ? '#dc2626' : '#1f2937', fontWeight: isMismatch ? 600 : 400, padding: '2px 6px', borderRadius: 6, background: isMismatch ? '#fee2e2' : 'transparent' }}>
                              {fa.udiseValue || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>—</span>}
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid #f0f0f0', paddingTop: 14, paddingBottom: 6 }}>
        <div style={{ fontSize: 13, color: '#64748b' }}>
          Showing <span style={{ fontWeight: 700, color: '#1e40af' }}>{filtered.length}</span> of {results.length} records
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button label="Export Report" icon="pi pi-download"
            style={{ borderRadius: 10, fontSize: 13, background: 'linear-gradient(135deg,#065f46,#059669)', border: 'none' }}
            onClick={() => exportReport(results, mappings)} />
          <Button label="Close" icon="pi pi-times" className="p-button-outlined"
            style={{ borderRadius: 10, fontSize: 13 }} onClick={onHide} />
        </div>
      </div>
    </Dialog>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT — UDISEAnomalyDetector
// ═══════════════════════════════════════════════════════════════════════════════

const UDISEAnomalyDetector: React.FC = () => {
  const toast = useRef<Toast>(null);

  const [classes, setClasses]                 = useState<ClassData[]>([]);
  const [students, setStudents]               = useState<StudentData[]>([]);
  const [loading, setLoading]                 = useState(false);
  const [selectedClass, setSelectedClass]     = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [showAllSections, setShowAllSections] = useState(false);

  const [udiseHeaders, setUdiseHeaders]           = useState<string[]>([]);
  const [udiseRows, setUdiseRows]                 = useState<UDISERow[]>([]);
  const [udiseClassField, setUdiseClassField]     = useState<string>('Class');
  const [udiseSectionField, setUdiseSectionField] = useState<string>('Section');
  const [udiseMatchField, setUdiseMatchField]     = useState<string>('Name');

  const [classMap, setClassMap]     = useState<Record<string, string>>({});
  const [mappings, setMappings]     = useState<FieldMapping[]>(DEFAULT_MAPPINGS);
  const [matchField, setMatchField] = useState<string>('name');

  const [modalView, setModalView] = useState<ModalView>('none');
  const [results, setResults]     = useState<StudentAnomaly[]>([]);

  const selectedClassHasSections = (): boolean => {
    if (!selectedClass) return false;
    const cls = classes.find(c => c.id === selectedClass);
    return cls ? Boolean(cls.sections?.length) : false;
  };

  const shouldFetch = (): boolean => {
    if (!selectedClass) return false;
    if (selectedClassHasSections()) return selectedSection !== null || showAllSections;
    return true;
  };

  useEffect(() => {
    if (shouldFetch()) fetchStudents();
    else setStudents([]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass, selectedSection, showAllSections]);

  const fetchStudents = async () => {
    if (!shouldFetch()) return;
    setLoading(true);
    try {
      const params: any = { page: 1, limit: 500, class_id: selectedClass };
      if (selectedSection && !showAllSections) params.section = selectedSection;
      const response = await apiClient.get('/get-students-list/class-section', { params });
      const data = response.data;
      if (data.status === 'success') setStudents(data.data);
      else toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch students', life: 4000 });
    } catch (err: any) {
      toast.current?.show({ severity: 'error', summary: 'Network Error', detail: err.response?.data?.message || 'An error occurred', life: 5000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (udiseRows.length === 0 || classes.length === 0) return;
    const udiseClasses = Array.from(new Set(udiseRows.map(r => r[udiseClassField] ?? '').filter(Boolean)));
    const autoMap: Record<string, string> = {};
    udiseClasses.forEach(uc => {
      if (!classMap[uc]) {
        const match = classes.find(c => normalizeClassName(c.class_name) === normalizeClassName(uc));
        if (match) autoMap[uc] = match.id;
      }
    });
    if (Object.keys(autoMap).length > 0) setClassMap(prev => ({ ...autoMap, ...prev }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [udiseRows.length, udiseClassField, classes.length]);

  const handleCSVParsed = (headers: string[], rows: UDISERow[]) => {
    setUdiseHeaders(headers);
    setUdiseRows(rows);
    const clsCol  = headers.find(h => h.toLowerCase() === 'class')   ?? headers.find(h => h.toLowerCase().includes('class'))   ?? headers[0];
    const secCol  = headers.find(h => h.toLowerCase() === 'section') ?? headers.find(h => h.toLowerCase().includes('section')) ?? '';
    const nameCol = headers.find(h => h.toLowerCase() === 'name')    ?? headers.find(h => h.toLowerCase().includes('name'))    ?? headers[0];
    if (clsCol)  setUdiseClassField(clsCol);
    if (secCol)  setUdiseSectionField(secCol);
    if (nameCol) setUdiseMatchField(nameCol);
    setModalView('class_mapping');
    toast.current?.show({ severity: 'success', summary: 'File Loaded', detail: `${rows.length} UDISE records loaded`, life: 3000 });
  };

  const runComparison = () => {
    const enabledMappings = mappings.filter(m => m.enabled && m.dbField && m.udiseField);

    const mappedUdiseClassValues = new Set(
      Object.entries(classMap).filter(([, dbId]) => Boolean(dbId)).map(([uc]) => uc)
    );
    const scopedUdiseClassValues: Set<string> = selectedClass
      ? new Set(Object.entries(classMap).filter(([, dbId]) => dbId === selectedClass).map(([uc]) => uc))
      : mappedUdiseClassValues;
    const effectiveScope = scopedUdiseClassValues.size > 0 ? scopedUdiseClassValues : mappedUdiseClassValues;

    let filteredUdiseRows = effectiveScope.size > 0
      ? udiseRows.filter(row => effectiveScope.has(row[udiseClassField] ?? ''))
      : [];

    if (udiseSectionField && selectedSection && !showAllSections) {
      filteredUdiseRows = filteredUdiseRows.filter(row =>
        normalize(row[udiseSectionField] ?? '') === normalize(selectedSection)
      );
    }

    const udiseLookup = new Map<string, UDISERow>();
    filteredUdiseRows.forEach(row => {
      const key = normalize(row[udiseMatchField] ?? '');
      if (key) udiseLookup.set(key, row);
    });

    const dbLookup = new Map<string, StudentData>();
    students.forEach(s => {
      const key = normalize(String((s as any)[matchField] ?? ''));
      if (key) dbLookup.set(key, s);
    });

    const anomalyResults: StudentAnomaly[] = [];

    students.forEach(student => {
      const key = normalize(String((student as any)[matchField] ?? ''));
      const udiseRow = udiseLookup.get(key);
      const anomalies: FieldAnomaly[] = enabledMappings.map(m => {
        const dbVal = String((student as any)[m.dbField] ?? '');
        const udiseVal = udiseRow ? (udiseRow[m.udiseField] ?? '') : '';
        let type: AnomalyType;
        if (!udiseRow)                                     type = 'missing_in_udise';
        else if (normalize(dbVal) === normalize(udiseVal)) type = 'match';
        else                                               type = 'mismatch';
        return { dbField: m.dbField, udiseField: m.udiseField, label: m.label || m.dbField, dbValue: dbVal, udiseValue: udiseVal, type };
      });
      const overallStatus: StudentAnomaly['overallStatus'] = !udiseRow
        ? 'missing_in_udise'
        : anomalies.some(a => a.type === 'mismatch') ? 'anomaly' : 'match';
      anomalyResults.push({ dbStudent: student, udiseRow: udiseRow || null, anomalies, matchKey: key, overallStatus });
    });

    filteredUdiseRows.forEach(row => {
      const key = normalize(row[udiseMatchField] ?? '');
      if (key && !dbLookup.has(key)) {
        anomalyResults.push({ dbStudent: null, udiseRow: row, anomalies: [], matchKey: key, overallStatus: 'missing_in_db' });
      }
    });

    setResults(anomalyResults);
    setModalView('results');

    const anomalyCount = anomalyResults.filter(r => r.overallStatus === 'anomaly').length;
    toast.current?.show({
      severity: anomalyCount > 0 ? 'warn' : 'success',
      summary: 'Comparison Done',
      detail: `${anomalyCount} anomalies found across ${anomalyResults.length} records`,
      life: 4000,
    });
  };

  const hasStudents = students.length > 0;
  const hasUDISE    = udiseRows.length > 0;

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui,-apple-system,sans-serif' }}>
        <GlobalHeaderTitle
          title="UDISE Anomaly Detector"
          subtitle="Upload UDISE data to compare with school records and detect discrepancies"
          icon="pi pi-shield"
          buttons={[
            ...(hasUDISE ? [{ label: 'Re-map', icon: 'pi pi-sliders-h', onClick: () => setModalView('class_mapping'), outlined: true }] : []),
            ...(hasStudents && hasUDISE ? [{
              label: 'Run Comparison', icon: 'pi pi-play', onClick: runComparison,
              style: { background: 'linear-gradient(135deg,#1e40af,#3b82f6)', border: 'none' },
            }] : []),
          ]}
        />

        <ClassSectionFilter
          selectedClass={selectedClass}
          selectedSection={selectedSection}
          onClassChange={cls => { setSelectedClass(cls); setSelectedSection(null); setShowAllSections(false); }}
          onSectionChange={sec => { setSelectedSection(sec); setShowAllSections(false); }}
          onClassesLoaded={cls => setClasses(cls)}
          allowAllSections
          showingAllSections={showAllSections}
          onShowAllSections={() => setShowAllSections(true)}
        />

        {/* Status bar */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          {[
            { icon: 'pi-database', label: 'School Database', value: loading ? '...' : hasStudents ? `${students.length} students` : 'Select class', active: hasStudents },
            { icon: 'pi-file',     label: 'UDISE File',      value: hasUDISE ? `${udiseRows.length} records` : 'Not uploaded', active: hasUDISE },
          ].map(({ icon, label, value, active }) => (
            <div key={label} style={{
              padding: '12px 16px', borderRadius: 14,
              background: active ? 'linear-gradient(135deg,#eff6ff,#dbeafe)' : '#f8fafc',
              border: `1.5px solid ${active ? '#bfdbfe' : '#e5e7eb'}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: active ? 'linear-gradient(135deg,#1e40af,#3b82f6)' : '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className={`pi ${icon}`} style={{ fontSize: 14, color: 'white' }} />
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 500, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.5px' }}>{label}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: active ? '#1e40af' : '#94a3b8' }}>{value}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Main content */}
        {loading ? (
          <StudentsListSkeletonLoader />
        ) : (
          <div style={{ background: 'white', borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,.05)', border: '1px solid #e5e7eb', padding: 22 }}>
            {!hasStudents && (
              <div style={{ marginBottom: 18, padding: '13px 16px', borderRadius: 12, background: '#fffbeb', border: '1.5px solid #fde68a', color: '#92400e', fontSize: 13, fontWeight: 500 }}>
                <i className="pi pi-exclamation-triangle" style={{ marginRight: 6 }} />
                Please select a class (and section if needed) above to load school students before uploading UDISE data.
              </div>
            )}

            <CSVUploadStep onParsed={handleCSVParsed} />

            {results.length > 0 && (
              <div style={{ marginTop: 18, padding: '14px 16px', borderRadius: 12, background: '#eff6ff', border: '1.5px solid #bfdbfe' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1e40af' }}>Last comparison ran successfully</div>
                    <div style={{ fontSize: 12, color: '#3b82f6', marginTop: 2 }}>
                      {results.filter(r => r.overallStatus === 'anomaly').length} anomalies · {results.length} total records
                    </div>
                  </div>
                  <button onClick={() => setModalView('results')} style={{
                    padding: '8px 16px', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                    background: 'linear-gradient(135deg,#1e40af,#3b82f6)', color: 'white', border: 'none',
                  }}>
                    <i className="pi pi-eye" style={{ marginRight: 6 }} />View Report
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      <ClassMappingModal
        visible={modalView === 'class_mapping'} onHide={() => setModalView('none')}
        udiseRows={udiseRows} udiseHeaders={udiseHeaders}
        classes={classes} students={students}
        udiseClassField={udiseClassField} onUdiseClassFieldChange={setUdiseClassField}
        udiseSectionField={udiseSectionField} onUdiseSectionFieldChange={setUdiseSectionField}
        classMap={classMap} onClassMapChange={setClassMap}
        onNext={() => setModalView('field_mapping')}
      />

      <FieldMappingModal
        visible={modalView === 'field_mapping'} onHide={() => setModalView('none')}
        onBack={() => setModalView('class_mapping')}
        udiseRows={udiseRows} udiseHeaders={udiseHeaders} students={students}
        mappings={mappings} onMappingsChange={setMappings}
        matchField={matchField} onMatchFieldChange={setMatchField}
        udiseMatchField={udiseMatchField} onUdiseMatchFieldChange={setUdiseMatchField}
        onRunComparison={runComparison}
      />

      <AnomalyResultsModal
        visible={modalView === 'results'} onHide={() => setModalView('none')}
        results={results} mappings={mappings}
      />
    </>
  );
};

export default UDISEAnomalyDetector;